import React, { useState } from 'react'
import { Alert, Button, Card, Input } from 'reactstrap'
import { emailRegex } from 'shared/utils'
import { maskEmail } from 'shared/Masks'
import cognito from 'shared/cognito'
import { setCookie } from 'shared/cookie'
import PropTypes from 'prop-types'
import AuthFormHeader from '../AuthFormHeader'
import {
  IncorrectEmailMessage,
  PartnerSiteRedirectWrapper,
} from 'shared/components'

const LoginFormDisasterRecovery = () => {
  const [allAttemptsFailed, setAllAttemptsFailed] = useState(false)
  const [attempts, setAttempts] = useState(0)
  const [emailEntered, setEmailEntered] = useState(false)
  const [codeFailed, setCodeFailed] = useState(false)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [processing, setProcessing] = useState(false)
  const [disabled, setDisabled] = useState(true)
  const [invalidEmail, setInvalidEmail] = useState(false)
  const [sessionId, setSessionId] = useState(null)
  const [accessToken, setAccessToken] = useState(null)
  const [idToken, setIdToken] = useState(null)
  const [redirect, setRedirect] = useState(false)

  const handleEmailSubmit = async () => {
    try {
      setProcessing(true)
      const results = await cognito.handlePasswordLess(email.toLowerCase())
      if (results.Session) {
        setEmailEntered(true)
        setInvalidEmail(false)
        setSessionId(results.Session)
      }

      setDisabled(true)
      setCode('')
      setAllAttemptsFailed(false)
      setAttempts(0)
      setProcessing(false)
    } catch (ex) {
      setInvalidEmail(true)
      setProcessing(false)
    }
  }
  const handleCodeSubmit = async () => {
    try {
      setProcessing(true)
      const results = await cognito.handleRespondToAuthChallenge(
        sessionId,
        email.toLowerCase(),
        code
      )
      if (results.AuthenticationResult?.AccessToken) {
        setIdToken(results.AuthenticationResult?.IdToken)
        setAccessToken(results.AuthenticationResult?.AccessToken)
        setCookie(
          document,
          '',
          'access_token',
          results.AuthenticationResult?.AccessToken,
          60
        )
        setCookie(
          document,
          '',
          'id_token',
          results.AuthenticationResult?.IdToken,
          60
        )

        let locationObject = {
          protocol: window.location.protocol,
          host: window.location.host,
          hash: window.location.hash,
        }

        await cognito.singleSignOn(locationObject)
        setRedirect(true)
      } else {
        if (attempts + 1 >= maxAttempts) {
          setAllAttemptsFailed(true)
          setEmailEntered(false)
          setDisabled(true)
          setEmail('')
          setAttempts(0)
          return
        }
        setCode('')
        setCodeFailed(true)
        setSessionId(results.Session)
      }
    } catch (ex) {
      if (attempts + 1 >= maxAttempts) {
        setAllAttemptsFailed(true)
        setEmailEntered(false)
        setDisabled(true)
        setEmail('')
        setAttempts(0)
        return
      }
      setCode('')
      setCodeFailed(true)
    }
    setAttempts(attempts + 1)
    setProcessing(false)
  }

  const onEmailChange = ({ target: { value } }) => {
    if (emailRegex.test(value)) {
      setDisabled(false)
    } else {
      setDisabled(true)
    }
    setEmail(value)
  }

  const onCodeChange = ({ target: { value } }) => {
    if (value.length < 6 && /^\d{0,5}$/.test(value)) {
      setDisabled(true)
    } else {
      setCode(value)
      setDisabled(false)
    }
  }

  if (redirect) {
    return (
      <PartnerSiteRedirectWrapper
        isAppRedirect={false}
        redirectToP360={true}
        authProps={{
          accessToken: accessToken,
          idToken: idToken,
          isAdmin: false,
        }}
        isInline={true}
      />
    )
  }

  if (allAttemptsFailed) {
    return (
      <CardAndAuthFormHeader title="You have exceeded the maximum number of attempts.">
        <p>
          Unfortunately, we're unable to verify your account. For your
          protection, you are not able to resume you application at this time.
        </p>
        <p>
          For further assistance, call{' '}
          <a href={`tel:800.723.7022`}>800.723.7022.</a>
        </p>
      </CardAndAuthFormHeader>
    )
  }

  if (emailEntered) {
    return (
      <CardAndAuthFormHeader
        title="To help keep your account safe, please enter the verification code that
                was sent to the email address below.  It may take up to 2 minutes to 
                receive the code."
      >
        <p className="text-center">
          <strong>{emailEntered && maskEmail(email)}</strong>
        </p>

        <Input
          onChange={onCodeChange}
          placeholder="Enter Code"
          type="number"
          onInput={e => (e.target.value = e.target.value.slice(0, 10))}
        />

        {codeFailed && (
          <Alert color="danger" data-test="error">
            <p>
              Verification unsuccessful. Please confirm your code was entered
              correctly and try again.
            </p>
          </Alert>
        )}

        <ButtonWithProcessing
          onClick={handleCodeSubmit}
          disabled={disabled}
          processing={processing}
        >
          VERIFY CODE
        </ButtonWithProcessing>
      </CardAndAuthFormHeader>
    )
  }

  return (
    <React.Fragment>
      <CardAndAuthFormHeader title="Please enter your email address to sign in.">
        <Input
          onChange={onEmailChange}
          placeholder="Enter Email"
          value={email}
        />

        <IncorrectEmailMessage isOpen={invalidEmail} />

        <ButtonWithProcessing
          onClick={handleEmailSubmit}
          disabled={disabled}
          processing={processing}
        >
          SEND Code
        </ButtonWithProcessing>
      </CardAndAuthFormHeader>
    </React.Fragment>
  )
}

function CardAndAuthFormHeader({ children, title }) {
  return (
    <Card body className="mb-3 mt-5 auth-block gap-b">
      <AuthFormHeader headerMessage={title} />
      {children}
    </Card>
  )
}

CardAndAuthFormHeader.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
}

function ButtonWithProcessing({ processing, disabled, onClick, children }) {
  return (
    <Button
      className="w-100 mt-3 pocket360-button"
      onClick={onClick}
      disabled={processing || disabled}
    >
      {children}
    </Button>
  )
}

const maxAttempts = 3

ButtonWithProcessing.propTypes = {
  processing: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
}

export default LoginFormDisasterRecovery
